import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { deviceStore } from '@/stores/deviceStore';
import { DeviceCard } from './DeviceCard';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { Loader2 } from 'lucide-react';

export const DeviceGrid = observer(() => {
  const { t } = useTranslation();
  const { devicesByArea, loading, error } = deviceStore;
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter devices based on search term
  const filteredAreaGroups = () => {
    if (!searchTerm.trim()) return devicesByArea;
    
    const filtered: typeof devicesByArea = {};
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    
    Object.keys(devicesByArea).forEach(area => {
      const filteredDevices = devicesByArea[area].filter(device => 
        (device.device_id || '').toLowerCase().includes(lowerSearchTerm) ||
        (device.pillarno || '').toString().toLowerCase().includes(lowerSearchTerm) ||
        (device.area || '').toLowerCase().includes(lowerSearchTerm) ||
        (device.point || '').toLowerCase().includes(lowerSearchTerm)
      );
      
      if (filteredDevices.length > 0) {
        filtered[area] = filteredDevices;
      }
    });
    
    return filtered;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-md">
        {error}
      </div>
    );
  }
  
  const areaGroups = filteredAreaGroups();
  const sortedAreas = Object.keys(areaGroups).sort((a, b) => 
    a.localeCompare(b, undefined, {sensitivity: 'base'})
  );
  
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('dashboard.searchDevices')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      
      {sortedAreas.length === 0 ? (
        <div className="text-center text-gray-500 p-8">
          {t('dashboard.noDevicesFound')}
        </div>
      ) : (
        sortedAreas.map(area => (
          <div key={area} className="space-y-3">
            <h3 className="text-lg font-semibold">{area}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {areaGroups[area].map(device => (
                <DeviceCard key={device.device_id} device={device} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}); 